import { Trans } from '@lingui/macro';
import { Download, Pin } from 'lucide-react';
import { useEffect, useState } from 'react';
import { RoomItem } from '../../models/RoomItem';
import { Fire } from '../../services/FireClient';
import { My } from '../../services/My';

import { FileIconThumb } from './MessageFileItem';
import { MouseOverPreview } from './ItemFilePopover';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Progress } from "../../components/ui/progress";
import { cn } from "../../lib/utils";

interface IAddFromMyRoomFilesModal { onCancel: () => void, onDone: (selectedItems: RoomItem[]) => void };

export const AddFromMyRoomFilesModal: React.FC<IAddFromMyRoomFilesModal> = ({ onCancel, onDone }) => {
  const [selectedItems, setSelectedItems] = useState<RoomItem[]>([]);
  const [myRoomItems, setMyRoomItems] = useState<RoomItem[]>([]);
  const [inProgress, setInProgress] = useState(false);

  function close() {
    onDone(selectedItems);
  }

  function toggleSelection(item: RoomItem) {
    if (selectedItems.find(i => i.id === item.id)) {
      setSelectedItems(selectedItems.filter(i => i.id !== item.id));
    } else {
      setSelectedItems([...selectedItems, item]);
    }
  }

  useEffect(() => {
    setInProgress(true);

    // 현재 유저의 모든 Personal Room 찾기 (workspaceId_userId 패턴)
    const personalRooms = Array.from(My.Rooms.values()).filter(
      r => r.visibility === 'personal' && r.userIds.includes(My.user.id)
    );

    if (personalRooms.length === 0) {
      setMyRoomItems([]);
      setInProgress(false);
      return;
    }

    // 각 Personal Room의 파일을 구독
    const itemsByRoom = new Map<string, RoomItem[]>();
    const unsubs: (() => void)[] = [];

    personalRooms.forEach(room => {
      const unsub = Fire.item.subscribeToRoomItemsByType(room.id, ['file'], items => {
        itemsByRoom.set(room.id, items);
        // 모든 방의 파일을 합쳐서 날짜순 정렬
        const allItems = Array.from(itemsByRoom.values()).flat();
        allItems.sort((a, b) => {
          const aTime = a.created instanceof Date ? a.created.getTime() : new Date(a.created).getTime();
          const bTime = b.created instanceof Date ? b.created.getTime() : new Date(b.created).getTime();
          return bTime - aTime;
        });
        setMyRoomItems(allItems);
        setInProgress(false);
      });
      unsubs.push(unsub);
    });

    return () => unsubs.forEach(u => u());
  }, [])

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-3xl h-[80vh] flex flex-col p-0 z-[2000] gap-0">
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <DialogTitle><Trans>Select files from My Channel</Trans></DialogTitle>
        </DialogHeader>

        {inProgress && <Progress value={undefined} className="h-0.5 w-full rounded-none" />}

        <div className="flex-1 overflow-hidden relative">
          <div className="h-full overflow-y-auto">
            <div className="px-6 pb-20 divide-y divide-border">
              {myRoomItems.map(item => (
                <FileItemRow
                  key={item.id}
                  item={item}
                  isSelected={!!selectedItems.find(i => i.id === item.id)}
                  onToggle={() => toggleSelection(item)}
                />
              ))}
              {!inProgress && myRoomItems.length == 0 && (
                <p className="text-center text-muted-foreground mt-10"><Trans>You have no files in My Channel</Trans></p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t bg-muted/50 shrink-0 flex flex-row justify-between sm:justify-between">
          <Button variant="outline" onClick={onCancel}><Trans>Cancel</Trans></Button>
          <Button onClick={close} disabled={selectedItems.length === 0}>
            <Trans>Done</Trans> {selectedItems.length > 0 && `(${selectedItems.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const FileItemRow: React.FC<{ item: RoomItem, isSelected: boolean, onToggle: () => void }> = ({ item, isSelected, onToggle }) => {
  const itemTitle = () => (
    <div className="flex items-center gap-2">
      <div className="shrink-0 scale-75 origin-left">
        <FileIconThumb filename={item.title ?? ''} url={item.url ?? ''} />
      </div>
      <a
        href={item.url}
        target={'_blank'}
        onClick={(e) => e.stopPropagation()}
        className="text-sm font-medium hover:underline truncate max-w-[300px] text-foreground"
      >
        {item.title ?? item.url}
      </a>
      {item.pinned && <Pin className="w-3 h-3 text-muted-foreground rotate-45" />}
    </div>
  );

  return (
    <div
      className={cn(
        "flex items-center gap-4 py-3 px-2 transition-all cursor-pointer hover:bg-muted/50",
        isSelected && "bg-primary/15"
      )}
      onClick={onToggle}
    >
      <Checkbox checked={isSelected} onCheckedChange={onToggle} />

      <div className="flex-1 min-w-0">
        <MouseOverPreview imageUrl={item.url} content={itemTitle} />
      </div>

      <div className="w-32 text-sm text-muted-foreground truncate hidden md:block">
        {item.created instanceof Date
          ? item.created.toLocaleDateString()
          : new Date(item.created).toLocaleDateString()}
      </div>

      {/* Downloads count if needed (currently mocked as 0 or need standard props, assuming user didnt ask for downloads logic here specifically but preserving visuals if easy. 
                Original passed 'downloads={[]}' so it was likely hidden. Omitting.) 
            */}
    </div>
  )
}
